import React, { useEffect, useState } from "react";
import {
  Layout,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Card,
  Typography,
  Popconfirm,
  Switch,
  Upload,
  Rate,
  Avatar,
  message,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DragOutlined,
} from "@ant-design/icons";

import {
  createFeedback,
  deleteFeedback,
  getFeedbacks,
  updateFeedback,
  updateFeedbackImage,
  updateFeedbackPriority,
  updatePublishStatus,
} from "../services/feedback.service";

import ImageGridSorter from "./ImageGridSorter";
import "./feedback.css";

const { Content } = Layout;
const { Title, Text } = Typography;

const Feedback = () => {
  const [form] = Form.useForm();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const [isOrderView, setIsOrderView] = useState(false);
  const [orderedData, setOrderedData] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  // Fetch

  const fetchFeedback = async (page = 1, limit = 5) => {
    try {
      setLoading(true);

      const res = await getFeedbacks(page, limit);

      setData(res.data.data);

      setPagination({
        current: page,
        pageSize: limit,
        total: res.data.pagination.totalItems,
      });
    } catch {
      message.error("Failed to fetch feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback(1, 5);
  }, []);

  // Create

  const handleSaveOrder = async () => {
    try {
      const source = orderedData.length ? orderedData : data;

      const reorderedItems = source.map((item, index) => ({
        id: item.id,
        priority: index,
      }));

      await updateFeedbackPriority({
        reorderedItems,
      });

      message.success("Feedback order updated successfully");

      setOrderedData([]);
    } catch {
      message.error("Failed to update order");
    }
  };
  const handleCreate = async (values) => {
    try {
      await createFeedback(values);

      message.success("Feedback added");

      setIsModalVisible(false);

      fetchFeedback(pagination.current, pagination.pageSize);
    } catch {
      message.error("Create failed");
    }
  };

  // Update

  const handleUpdate = async (values) => {
    try {
      await updateFeedback(editingItem.id, values);

      message.success("Feedback updated");

      setIsModalVisible(false);

      fetchFeedback(pagination.current, pagination.pageSize);
    } catch {
      message.error("Update failed");
    }
  };

  // Delete

  const handleDelete = async (id) => {
    await deleteFeedback(id);

    message.success("Deleted");

    fetchFeedback(pagination.current, pagination.pageSize);
  };

  // Publish

  const togglePublish = async (id, checked) => {
    try {
      await updatePublishStatus(id, { isPublish: checked });

      message.success(
        checked ? "Published Successfully" : "Unpublished Successfully",
      );

      fetchFeedback(pagination.current, pagination.pageSize);
    } catch {
      message.error("Publish update failed");
    }
  };

  // Image update

  const updateImage = async (id, file) => {
    const reader = new FileReader();

    reader.onload = async () => {
      await updateFeedbackImage(id, {
        customerImage: reader.result,
      });

      message.success("Image updated");

      fetchFeedback(pagination.current, pagination.pageSize);
    };

    reader.readAsDataURL(file);
  };

  // Table

  const columns = [
    {
      title: "Image",
      dataIndex: "customerImage",
      render: (img, record) => (
        <Space direction="vertical">
          <Avatar size={70} src={img} />

          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              updateImage(record.id, file);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Update
            </Button>
          </Upload>
        </Space>
      ),
    },

    {
      title: "Name",
      dataIndex: "customerName",
      render: (text) => <Text strong>{text}</Text>,
    },

    {
      title: "Designation",
      dataIndex: "customerDesignation",
    },

    {
      title: "Rating",
      dataIndex: "customerratings",
      render: (rate) => <Rate disabled value={rate} />,
    },

    {
      title: "Review",
      dataIndex: "customerReview",
      width: "35%",
    },

    {
      title: "Publish",
      render: (_, record) => (
        <Switch
          checked={record.isPublish}
          onChange={(checked) => togglePublish(record.id, checked)}
        />
      ),
    },

    {
      title: "Actions",
      render: (_, record) => (
        <Space direction="vertical">
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingItem(record);

              form.setFieldsValue({
                ...record,
                isPublish: record.isPublish ? true : false,
              });

              setIsModalVisible(true);
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete feedback?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="feedback-layout">
      <Content className="feedback-content">
        <Card title={<Title level={4}>Customer Reviews</Title>}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
           onClick={() => {
  setEditingItem(null);
  form.resetFields();

  setImagePreview(null); // ✅ old image clear

  setIsModalVisible(true);
}}
          >
            Add Feedback
          </Button>

          <Button
            style={{ marginLeft: 20 }}
            icon={<DragOutlined />}
            onClick={async () => {
              if (!isOrderView) {
                // Fetch all feedbacks when reorder opens

                const res = await getFeedbacks(1, 1000);

                const allFeedbacks = res?.data?.data || [];

                setData(allFeedbacks);

                setOrderedData(
                  allFeedbacks.map((i) => ({
                    id: i.id,
                    image: i.customerImage,
                    title: i.customerName,
                    subtitle: i.customerDesignation,
                  })),
                );
              } else {
                // back to paginated list

                fetchFeedback(pagination.current, pagination.pageSize);
              }

              setIsOrderView(!isOrderView);
            }}
          >
            {isOrderView ? "Back to List" : "Reorder"}
          </Button>
          {isOrderView && (
            <Button
              type="primary"
              style={{ marginLeft: 10 }}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          )}
          <br />
          <br />

          {isOrderView && (
            <ImageGridSorter
              data={data.map((i) => ({
                id: i.id,
                image: i.customerImage,
                title: i.customerName,
                subtitle: i.customerDesignation,
              }))}
              onChange={(newOrder) => {
                setOrderedData(newOrder);

                const updated = newOrder.map((item) =>
                  data.find((d) => d.id === item.id),
                );

                setData(updated);
              }}
            />
          )}

          {!isOrderView && (
            <Table
              columns={columns}
              dataSource={data}
              rowKey="id"
              loading={loading}
              scroll={{ x: 700 }}
              pagination={pagination}
              onChange={(p) => {
                fetchFeedback(p.current, p.pageSize);
              }}
            />
          )}
        </Card>
      </Content>

      {/* Model */}

      <Modal
        title={editingItem ? "Edit Feedback" : "Add Feedback"}
        open={isModalVisible}
        footer={null}
       onCancel={() => {
  setIsModalVisible(false);
  form.resetFields();
  setImagePreview(null); // ✅ reset
}}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingItem ? handleUpdate : handleCreate}
        >
          <Form.Item
            name="customerName"
            label="Customer Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="customerDesignation"
            label="Customer Designation"
            rules={[{ required: true }]}
          >
            <Input.TextArea />
          </Form.Item>

          <Form.Item
            name="customerReview"
            label="Review"
            rules={[{ required: true }]}
          >
            <Input.TextArea />
          </Form.Item>

          <Form.Item
            name="customerratings"
            label="Rating"
            rules={[{ required: true }]}
          >
            <Rate />
          </Form.Item>
          {!editingItem && (
            <Form.Item
              name="customerImage"
              label="Customer Image"
              rules={[{ required: true, message: "Please upload image" }]}
            >
              <Upload
  showUploadList={false}
  beforeUpload={(file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const base64 = e.target.result;

      form.setFieldsValue({
        customerImage: base64,
      });

      setImagePreview(base64); // ✅ preview set
    };

    reader.readAsDataURL(file);

    return false;
  }}
>
  <Button icon={<UploadOutlined />}>Upload Image</Button>
</Upload>

{/* ✅ Preview Image */}
{imagePreview && (
  <div style={{ marginTop: 10 }}>
    <img
      src={imagePreview}
      alt="preview"
      style={{ width: 80, height: 80, borderRadius: 8 }}
    />
  </div>
)}
            </Form.Item>
          )}
          {!editingItem && (
            <Form.Item
              name="isPublish"
              label="Publish"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingItem ? "Update" : "Save"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Feedback;
